import { useBlockProps, InspectorControls } from '@wordpress/block-editor'
import { PanelBody, Button, PanelRow, __experimentalUnitControl as UnitControl, ResizableBox, ButtonGroup, ToggleControl } from '@wordpress/components'
import { useState, useEffect } from '@wordpress/element'
import { useInstanceId } from '@wordpress/compose'
import { useSelect } from '@wordpress/data'

import { styleBreakpoints } from './lib/styleBreakpoints'
import { DEFAULT_BREAKPOINT_SIZES } from './lib/constants'

import './styles.scss'
import './editor.scss'

export default function Edit(props) {
  const { attributes, setAttributes, clientId } = props
  const { blockId, blockSizes = {}, isolationMode, breakPointSizes, doSiblingMaxWidth, lastEditedBreakpoint } = attributes
  const [activeBreakpoint, setActiveBreakpoint] = useState('small')
  const [currentSize, setCurrentSize] = useState({})
  const [maxWidth, setMaxWidth] = useState(0)
  const [maxHeight, setMaxHeight] = useState(0)

  const uniqueId = useInstanceId(Edit, 'dualaxisspacer')

  const isSelected = useSelect(
    select => {
      const { getSelectedBlockClientId } = select('core/block-editor')
      return getSelectedBlockClientId() === clientId
    },
    [clientId]
  )
  useEffect(() => {
    setAttributes({ blockId: uniqueId })
  }, [])

  useEffect(() => {
    if (activeBreakpoint) {
      // Update maxWidth and maxHeight when a breakpoint is selected
      setMaxWidth(blockSizes[activeBreakpoint]?.width || 0)
      setMaxHeight(blockSizes[activeBreakpoint]?.height || 0)
    } else if (!isolationMode) {
      // Calculate the maximum width and height across all breakpoints when isIsolated is false
      const maxWidthAcrossBreakpoints = Math.max(blockSizes.small?.width || 0, blockSizes.medium?.width || 0, blockSizes.large?.width || 0)
      const maxHeightAcrossBreakpoints = Math.max(blockSizes.small?.height || 0, blockSizes.medium?.height || 0, blockSizes.large?.height || 0)
      setMaxWidth(maxWidthAcrossBreakpoints)
      setMaxHeight(maxHeightAcrossBreakpoints)
    }
  }, [blockSizes, isolationMode])

  useEffect(() => {
    if (activeBreakpoint) {
      setAttributes({ lastEditedBreakpoint: activeBreakpoint })
    }
  }, [activeBreakpoint])

  useEffect(() => {
    if (isSelected) {
      setActiveBreakpoint(lastEditedBreakpoint)
    } else {
      setActiveBreakpoint(null)
    }
  }, [isSelected])

  function generateInvisibleElements() {
    return <div style={{ width: maxWidth, height: maxHeight }}></div>
  }

  function matchAllSizes(axis) {
    const max = Math.max(blockSizes.small?.[axis] || 0, blockSizes.medium?.[axis] || 0, blockSizes.large?.[axis] || 0)
    setAttributes({
      blockSizes: {
        small: { ...blockSizes.small, [axis]: max },
        medium: { ...blockSizes.medium, [axis]: max },
        large: { ...blockSizes.large, [axis]: max }
      }
    })
  }

  return (
    <div {...useBlockProps()}>
      <InspectorControls>
        <PanelBody title="Breakpoints" initialOpen={true}>
          <PanelRow>
            <div style={{ display: 'grid', gridTemplateColumns: '0.66fr 1fr 1fr', gap: '1rem', flexDirection: 'row', width: '100%' }}>
              <div style={{ display: 'flex', gap: '0.25rem', flexDirection: 'column', width: '100%' }}>
                <label>Small</label>
                <p style={{ overflow: 'hidden', marginTop: 'calc(0.375em - 1px)', display: 'flex', height: 'auto', alignItems: 'center', fontSize: '0.88rem' }}>{`< ${breakPointSizes.tablet?.toString().replace('px', '')}`}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexDirection: 'column', width: '100%' }}>
                <label>Medium</label>
                <UnitControl
                  value={breakPointSizes.tablet.toString().replace('px', '')}
                  onChange={newSize => {
                    setAttributes({
                      breakPointSizes: {
                        ...breakPointSizes,
                        tablet: newSize
                      }
                    })
                  }}
                  units={[{ value: 'px', label: 'px' }]}
                />{' '}
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexDirection: 'column', width: '100%' }}>
                <label>Large</label>
                <UnitControl
                  value={breakPointSizes.desktop.toString().replace('px', '')}
                  onChange={newSize => {
                    setAttributes({
                      breakPointSizes: {
                        ...breakPointSizes,
                        desktop: newSize
                      }
                    })
                  }}
                  units={[{ value: 'px', label: 'px' }]}
                />{' '}
              </div>
            </div>
          </PanelRow>
          <Button isSecondary onClick={() => setAttributes({ breakPointSizes: DEFAULT_BREAKPOINT_SIZES })}>
            Reset defaults
          </Button>
        </PanelBody>
        <PanelBody title="Active Breakpoint" initialOpen={true}>
          <ButtonGroup>
            {['small', 'medium', 'large'].map(breakpoint => (
              <Button key={breakpoint} isPrimary={activeBreakpoint === breakpoint} onClick={() => setActiveBreakpoint(breakpoint)}>
                {breakpoint.charAt(0).toUpperCase() + breakpoint.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
        </PanelBody>
        <PanelBody title="Sizes" initialOpen={true}>
          {['small', 'medium', 'large'].map(breakpoint => (
            <PanelRow key={breakpoint}>
              <div className="control-group__label">{breakpoint.charAt(0).toUpperCase() + breakpoint.slice(1)}</div>
              <div className="control-group">
                <UnitControl
                  key="width"
                  label={`Width`}
                  units={[{ value: 'px', label: 'px' }]}
                  value={blockSizes[breakpoint]?.width}
                  onChange={newValue => {
                    setAttributes({
                      blockSizes: {
                        ...blockSizes,
                        [breakpoint]: {
                          ...blockSizes[breakpoint],
                          width: newValue
                        }
                      }
                    })
                  }}
                />
                <UnitControl
                  key="height"
                  label={`Height`}
                  units={[{ value: 'px', label: 'px' }]}
                  value={blockSizes[breakpoint]?.height}
                  onChange={newValue => {
                    setAttributes({
                      blockSizes: {
                        ...blockSizes,
                        [breakpoint]: {
                          ...blockSizes[breakpoint],
                          height: newValue
                        }
                      }
                    })
                  }}
                />
              </div>
            </PanelRow>
          ))}
        </PanelBody>
        <PanelBody title="Block Settings" initialOpen={true}>
          <ToggleControl
            label="Isolate current size"
            checked={isolationMode}
            onChange={isolationMode => {
              setAttributes({ isolationMode })
            }}
          />
          <ToggleControl label="Sibling Max Width" checked={doSiblingMaxWidth} onChange={newVal => setAttributes({ doSiblingMaxWidth: newVal })} />

          <PanelRow>
            <div className="control-group__label">Match size across breakpoints</div>
            <Button isSecondary onClick={() => matchAllSizes('width')}>
              Width
            </Button>
            {' '}
            <Button isSecondary onClick={() => matchAllSizes('height')}>
              Height
            </Button>
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      {isSelected ? (
        <>
          <div className={`dualaxispacer-container is-selected`} style={{ display: 'grid' }}>
            <div style={{ gridArea: '1 / 1 / 2 / 2' }}>{generateInvisibleElements(blockSizes, isolationMode)}</div>{' '}
            <div style={{ gridArea: '1 / 1 / 2 / 2' }}>
              {['large', 'medium', 'small'].map(breakpoint => {
                const nonactiveSpacers = (
                  <div data-breakpoint={`${breakpoint[0].toUpperCase()}`} onClick={() => setActiveBreakpoint(breakpoint)} style={{ width: blockSizes[breakpoint].width, height: blockSizes[breakpoint].height }} className={`dualaxisspacer-edit--unit dualaxisspacer-edit--${breakpoint}`}>
                    {/* ... */}
                  </div>
                )
                return activeBreakpoint === breakpoint ? (
                  <ResizableBox
                    onBlur={() => setActiveBreakpoint(null)}
                    style={{ zIndex: 1000 }}
                    data-breakpoint={`${breakpoint[0].toUpperCase()}`}
                    className={`dualaxisspacer-edit--unit dualaxisspacer-edit--${breakpoint} active`}
                    key={breakpoint}
                    size={blockSizes[breakpoint]}
                    minHeight="0"
                    minWidth="0"
                    enable={{
                      bottom: true,
                      right: true,
                      bottomRight: true
                    }}
                    onResize={(event, direction, elt, delta) => {
                      const newSize = {
                        width: elt.style.width,
                        height: elt.style.height
                      }
                      setCurrentSize(newSize)
                    }}
                    onResizeStop={() => {
                      setAttributes({
                        blockSizes: {
                          ...blockSizes,
                          [activeBreakpoint]: {
                            width: parseInt(currentSize.width),
                            height: parseInt(currentSize.height)
                          }
                        }
                      })
                    }}></ResizableBox>
                ) : (
                  nonactiveSpacers
                )
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* JSX to render when isSelected is false */}
          {/* <div id={`dualaxisspacer-block--wrapper-${blockId}`} className="editor" {...useBlockProps.save()}> */}
          <div id={`dualaxisspacer-block--wrapper-${blockId}`} className="editor">
            <style>{styleBreakpoints(blockId, blockSizes, doSiblingMaxWidth, breakPointSizes)}</style>
            <div id={`${blockId}`} className="dualaxisspacer-block"></div>
          </div>
        </>
      )}
    </div>
  )
}
